import { useState, useEffect, useMemo } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { ListMerchantsWithStationCount } from 'reactRedux/thunks/merchnatThunk/merchantThunk';
import { flushSync } from 'react-dom';

import { normalizeForSearch } from 'utils/CommonFunction';

const useMerchantExclusionFilter = (excludeMerchant, setExcludeMerchant) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingExcludeMerchant, setPendingExcludeMerchant] = useState(
    excludeMerchant || [],
  );
  const [isLoadingMerchants, setIsLoadingMerchants] = useState(false);
  // State for excluding unassigned stations
  const [excludeUnassigned, setExcludeUnassigned] = useState(false);
  const [pendingExcludeUnassigned, setPendingExcludeUnassigned] =
    useState(false);

  const {
    isOpen: isMerchantPopoverOpen,
    onOpen: openMerchantPopover,
    onClose: closeMerchantPopover,
  } = useDisclosure();

  // Fetch merchants with station count on component mount
  const { merchantListData } = useSelector((state) => state.MerchantSlice);

  useEffect(() => {
    if (!Array.isArray(merchantListData) || merchantListData.length === 0) {
      setIsLoadingMerchants(true);
      dispatch(
        ListMerchantsWithStationCount({}, () => {
          setIsLoadingMerchants(false);
        }),
      );
    }
  }, [dispatch, merchantListData]);

  // Sync pending state when excludeMerchant prop changes
  useEffect(() => {
    setPendingExcludeMerchant(excludeMerchant || []);
  }, [excludeMerchant]);

  // Sync excludeUnassigned with pending state
  useEffect(() => {
    setPendingExcludeUnassigned(excludeUnassigned);
  }, [excludeUnassigned]);

  // Filter and sort merchants based on search term
  // Merchants should already be sorted by station count from API,
  // but we also sort by count as fallback
  const sortedMerchants = useMemo(() => {
    if (!Array.isArray(merchantListData)) return [];

    // merchantListData is already the array (not an object with .list)
    const merchants = [...merchantListData];

    // Sort by station count (highest first) if not already sorted
    merchants.sort((a, b) => (b.stationCount || 0) - (a.stationCount || 0));

    // Add displayName with station count, keep original name for search
    return merchants.map((merchant) => ({
      ...merchant,
      displayName: `${merchant.name} (${merchant.stationCount || 0} stations)`,
    }));
  }, [merchantListData]);

  const filteredMerchants = useMemo(() => {
    if (!searchTerm) return sortedMerchants;

    // Search against original name (without station count), accent-insensitive
    const normalizedTerm = normalizeForSearch(searchTerm);
    return sortedMerchants.filter(
      (merchant) =>
        merchant.name &&
        normalizeForSearch(merchant.name).includes(normalizedTerm),
    );
  }, [sortedMerchants, searchTerm]);

  // Helper functions for multiselect
  const handleLocalSelectChange = (newSelection) => {
    setPendingExcludeMerchant(newSelection);
  };

  const handleApplySelection = () => {
    // Use flushSync to ensure state is updated before popover closes
    flushSync(() => {
      // Commit the pending selection to the actual state
      setExcludeMerchant(pendingExcludeMerchant);
      setExcludeUnassigned(pendingExcludeUnassigned);
    });
    closeMerchantPopover();
  };

  const handleCancelSelection = () => {
    setPendingExcludeMerchant(excludeMerchant || []);
    setPendingExcludeUnassigned(excludeUnassigned);
    closeMerchantPopover();
  };

  const handleSelectAll = (filteredMerchantList) => {
    if (
      pendingExcludeMerchant &&
      pendingExcludeMerchant.length === filteredMerchantList?.length &&
      filteredMerchantList?.length > 0
    ) {
      // If all are selected, clear all including unassigned
      handleLocalSelectChange([]);
      setPendingExcludeUnassigned(false);
    } else {
      const fullMerchantList =
        sortedMerchants && sortedMerchants.length > 0
          ? sortedMerchants
          : filteredMerchantList || [];
      handleLocalSelectChange(fullMerchantList);
      setPendingExcludeUnassigned(true);
    }
  };

  const handleMerchantToggle = (merchant, isChecked) => {
    if (isChecked) {
      handleLocalSelectChange([...(pendingExcludeMerchant || []), merchant]);
    } else {
      handleLocalSelectChange(
        pendingExcludeMerchant?.filter(
          (m) => m.merchantId !== merchant.merchantId,
        ) || [],
      );
    }
  };

  const isSelectAllChecked = (filteredMerchantList) => {
    return (
      pendingExcludeMerchant &&
      pendingExcludeMerchant.length === filteredMerchantList?.length &&
      filteredMerchantList?.length > 0
    );
  };

  const isMerchantChecked = (merchant) => {
    return pendingExcludeMerchant?.some(
      (m) => m.merchantId === merchant.merchantId,
    );
  };

  const getSelectedMerchantText = () => {
    const parts = [];

    if (excludeUnassigned) {
      parts.push('Unassigned');
    }

    if (excludeMerchant && excludeMerchant.length > 0) {
      parts.push(
        excludeMerchant.length === 1
          ? excludeMerchant[0].name
          : `${excludeMerchant.length} merchants`,
      );
    }

    if (parts.length === 0) {
      return 'Exclude Merchants';
    }

    return `${parts.join(', ')} excluded`;
  };

  const handleClearSelection = () => {
    handleLocalSelectChange([]);
    setPendingExcludeUnassigned(false);
    setExcludeUnassigned(false);
  };

  // Handler for unassigned toggle
  const handleUnassignedToggle = (isChecked) => {
    setPendingExcludeUnassigned(isChecked);
  };

  return {
    searchTerm,
    setSearchTerm,
    pendingExcludeMerchant,
    setPendingExcludeMerchant,
    isMerchantPopoverOpen,
    openMerchantPopover,
    closeMerchantPopover,
    filteredMerchants,
    // The full enriched list, unaffected by the search box - a label chip must
    // select every merchant it covers, not just the ones matching the term.
    allMerchants: sortedMerchants,
    isLoadingMerchants,
    handleLocalSelectChange,
    handleApplySelection,
    handleCancelSelection,
    handleSelectAll,
    handleMerchantToggle,
    isSelectAllChecked,
    isMerchantChecked,
    getSelectedMerchantText,
    handleClearSelection,
    // Unassigned related
    excludeUnassigned,
    pendingExcludeUnassigned,
    handleUnassignedToggle,
    setExcludeUnassigned,
    setPendingExcludeUnassigned,
  };
};

export default useMerchantExclusionFilter;
